import { useState, type ReactNode } from 'react'
import { useListAssociatedParties } from '@/shared/hooks/api/associatedParties'
import type { AssociatedParty } from '@/shared/hooks/api/associatedParties'
import { Plus, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { AddAssociatedPartyModal } from './AddAssociatedPartyModal'
import { associatedPartiesCopyKey } from './associatedPartyCopy'
import {
  getAssociatedPartyDetailPath,
  isV2AssociatedPartiesShell,
} from './associatedPartyPaths'
import { formatAssociatedPartyAddress } from './formatAddress'

function AssociatedPartiesClassicChrome({
  children,
}: {
  children: ReactNode
}) {
  return (
    <>
      <Header fixed>
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>
      <Main className='flex flex-1 flex-col gap-4 sm:gap-6'>{children}</Main>
    </>
  )
}

export function AssociatedPartiesPage() {
  const { t } = useTranslation(['associatedParties', 'countries', 'common'])
  const { pathname } = useLocation()
  const embedInV2Shell = isV2AssociatedPartiesShell(pathname)
  const { data: parties = [], isLoading } = useListAssociatedParties()
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const navigate = useNavigate()

  const pageTitle = embedInV2Shell
    ? t('common:v2.pages.organisationsAndRoles.title')
    : t('title')
  const pageDescription = embedInV2Shell
    ? t('common:v2.pages.organisationsAndRoles.description')
    : t('description')

  const copy = (key: string) => t(associatedPartiesCopyKey(pathname, key))

  const openDetails = (partyId: string) => {
    navigate(getAssociatedPartyDetailPath(pathname, partyId))
  }

  const pageBody = isLoading ? (
    <div className='flex items-center justify-center py-12 text-muted-foreground'>
      {t('loading')}
    </div>
  ) : (
    <>
      <div className='flex flex-wrap items-end justify-between gap-2'>
        <div>
          <h2 className='text-2xl font-bold tracking-tight'>{pageTitle}</h2>
          <p className='text-muted-foreground'>{pageDescription}</p>
        </div>
        <Button onClick={() => setIsAddModalOpen(true)}>
          <Plus className='size-4' />
          {copy('addParty')}
        </Button>
      </div>

      {parties.length === 0 ? (
        <div className='flex flex-1 flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16'>
          <Users className='size-10 text-muted-foreground/50' />
          <div className='space-y-1 text-center'>
            <h3 className='text-lg font-semibold'>{copy('emptyState.title')}</h3>
            <p className='text-sm text-muted-foreground'>
              {copy('emptyState.description')}
            </p>
          </div>
          <Button onClick={() => setIsAddModalOpen(true)}>
            <Plus className='size-4' />
            {copy('emptyState.action')}
          </Button>
        </div>
      ) : (
        <div className='overflow-x-auto rounded-md border'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('list.columnName')}</TableHead>
                <TableHead>{t('list.columnAddress')}</TableHead>
                <TableHead>{t('list.columnContactName')}</TableHead>
                <TableHead>{t('list.columnContactPhoneNumber')}</TableHead>
                <TableHead>{t('list.columnContactEmail')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {parties.map((party: AssociatedParty) => (
                <TableRow
                  key={party.id}
                  className='cursor-pointer'
                  onClick={() => openDetails(party.id)}
                >
                  <TableCell>{party.name}</TableCell>
                  <TableCell
                    className='max-w-xs truncate'
                    title={formatAssociatedPartyAddress(party, {
                      getCountryName: (code) =>
                        t(code, { ns: 'countries' }) || code,
                      translate: (key, options) => t(key, options),
                    })}
                  >
                    {formatAssociatedPartyAddress(party, {
                      getCountryName: (code) =>
                        t(code, { ns: 'countries' }) || code,
                      translate: (key, options) => t(key, options),
                    })}
                  </TableCell>
                  <TableCell>
                    {party.contactName ?? t('emptyValue')}
                  </TableCell>
                  <TableCell>
                    {party.contactPhoneNumber ?? t('emptyValue')}
                  </TableCell>
                  <TableCell>
                    {party.contactEmail ?? t('emptyValue')}
                  </TableCell>
                  <TableCell
                    className='text-right'
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => openDetails(party.id)}
                    >
                      {t('common:actions.view')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  )

  const modals = (
    <AddAssociatedPartyModal
      open={isAddModalOpen}
      onOpenChange={setIsAddModalOpen}
    />
  )

  if (embedInV2Shell) {
    return (
      <div className='flex flex-col gap-4 sm:gap-6'>
        {pageBody}
        {modals}
      </div>
    )
  }

  return (
    <>
      <AssociatedPartiesClassicChrome>{pageBody}</AssociatedPartiesClassicChrome>
      {modals}
    </>
  )
}
